import { useContext, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';

import { FormField, FormHelp, Label, Textarea } from '@zillow/constellation-10';
import { cloneDeep } from 'es-toolkit';

import { multiFieldsUtil, UPDATE_CURRENT_PROPERTY_KEY } from '../../../utils/postingUtils';

import WizardServerError from '../../../model/WizardServerError';

import { DISPLAY_SERVER_ERROR_AT } from '../../../constants/WizardConstants';

import { ModularListingContext } from '../../../containers/CreateListingPage/context';
import { WizardContext } from '../../Wizard/context';

import { useForm } from '../../../hooks';

import WizardAlert from '../../WizardAlert';
import PropertyDescriptionStrength from '../../postingPath/PropertyDescriptionStrength';

import { postingConstraints } from '../../../utils/formValidation';

import actions from '../../../containers/CreateListingPage/actions';
import wizardActions from '../../Wizard/actions';
import PropertiesActions from '../../../actions/PropertiesActions';

const propTypes = {
  dataTestId: PropTypes.string,
  inputName: PropTypes.string.isRequired,
  label: PropTypes.string,
  passNullWhenEmptyString: PropTypes.bool,
  placeholder: PropTypes.string,
  initialValue: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  floorPlanId: PropTypes.string,
  required: PropTypes.bool,
  // the parent key where the data will be saved to via multi endpoint
  saveToParentKey: PropTypes.string.isRequired,
  // the key that determines how to update current property
  updateCurrentPropertyKey: PropTypes.string,
  includeDescriptionStrength: PropTypes.bool,
};

const defaultProps = {
  dataTestId: 'TextAreaFormField',
  label: '',
  passNullWhenEmptyString: false,
  placeholder: '',
  initialValue: '',
  floorPlanId: null,
  saveToParentKey: null,
  updateCurrentPropertyKey: null,
  includeDescriptionStrength: true,
};

const TextAreaFormField = ({
  dataTestId,
  initialValue,
  inputName,
  label,
  passNullWhenEmptyString,
  placeholder,
  floorPlanId,
  required,
  saveToParentKey,
  updateCurrentPropertyKey,
  includeDescriptionStrength,
}) => {
  const dispatch = useDispatch();

  const { reducerWizardDispatch } = useContext(WizardContext);

  // TODO: api is most likely changing from where to get values from (will not be floorplans)
  const { listingAlias, floorplans } = useSelector((state) => state.properties.currentProperty);
  const {
    modularListingState: { isInModalState, isSaveComplete, serverErrorObj, shouldSaveOnBlur },
    reducerDispatch,
  } = useContext(ModularListingContext);

  const prevValue = useRef();

  const floorplan = floorplans[0] || {};
  const inputValue = floorplan[inputName] || '';

  const displayError = serverErrorObj.fieldName === inputName && serverErrorObj.message !== '';

  const {
    inputs,
    errorObj,
    hasFieldError,
    handleChange: handleFormChange,
  } = useForm(
    {
      [inputName]: initialValue,
    },
    postingConstraints,
  );

  useEffect(() => {
    if (isSaveComplete) {
      save({
        target: {
          name: inputName,
          value: inputs[inputName],
        },
      });
    }
  }, [isSaveComplete]);

  useEffect(() => {
    updatePrevValueRef(initialValue);
  }, []);

  const clearServerError = () => {
    if (serverErrorObj.fieldName) {
      actions.removeServerError({
        reducerDispatch,
      });
    }
  };

  const save = (event) => {
    // return if user must click on cta
    if (!shouldSaveOnBlur && !isSaveComplete) {
      return;
    }

    const { value, name: fieldName } = event.target;

    const isValueChanging = value !== prevValue.current;

    if (hasFieldError(fieldName, value)) {
      actions.updateHasFieldError({ reducerDispatch, hasFieldError: true });
      actions.updateIsSaveComplete({ reducerDispatch, isSaveComplete: false });
      return;
    }

    if (isValueChanging === false) {
      actions.updateIsSaveComplete({ reducerDispatch, isSaveComplete: false });
      return;
    }

    // return if user must click on cta
    if (!shouldSaveOnBlur && !isSaveComplete) {
      return;
    }

    let valueToSave = value;

    if (value === '' && passNullWhenEmptyString) {
      valueToSave = null;
    }

    const dataToSave = multiFieldsUtil.getDataToSave({
      fieldName,
      floorPlanId,
      listingAlias,
      saveToParentKey,
      value: String(valueToSave),
    });

    wizardActions.updateIsFetchingData({ reducerWizardDispatch, isFetchingData: true });

    dispatch(PropertiesActions.saveMultipleFieldsPropertyInfo(dataToSave))
      .then(() => {
        updateCurrentProperty({ valueToSave });

        updatePrevValueRef(value);

        // reset save cta
        actions.updateIsSaveComplete({ reducerDispatch, isSaveComplete: false });
      })
      .catch((message) => {
        updateServerError(message, fieldName);
      })
      .finally(() => {
        wizardActions.updateIsFetchingData({
          reducerWizardDispatch,
          isFetchingData: false,
        });
      });
  };

  const updateCurrentProperty = ({ valueToSave }) => {
    if (updateCurrentPropertyKey === UPDATE_CURRENT_PROPERTY_KEY.ROOM_FOR_RENT_CURRENT_ROOMMATES_DETAILS) {
      dispatch(
        PropertiesActions.updateCurrentPropertyCurrentRoommatesDetails({
          [inputName]: String(valueToSave),
        }),
      );
    }

    if (updateCurrentPropertyKey === UPDATE_CURRENT_PROPERTY_KEY.FLOOR_PLANS_ROOM_FOR_RENT) {
      const copyFloorplan = cloneDeep(floorplans);
      copyFloorplan[0]['roomForRent'][inputName] = valueToSave;

      dispatch(PropertiesActions.updateCurrentProperty({ floorplans: copyFloorplan }));
    }
  };

  const updatePrevValueRef = (value) => {
    prevValue.current = value;
  };

  const updateServerError = (message = '', fieldName = '') => {
    const displayAt = isInModalState ? DISPLAY_SERVER_ERROR_AT.FIELD : DISPLAY_SERVER_ERROR_AT.PAGE;

    actions.updateServerError({
      reducerDispatch,
      serverErrorObj: WizardServerError({
        displayAt,
        fieldName,
        message,
        displayMessage: message,
      }),
    });
  };

  const handleChange = (event) => {
    clearServerError();
    handleFormChange(event);
  };

  return (
    <div data-testid={dataTestId}>
      <WizardAlert serverErrorObj={serverErrorObj} displayAt={DISPLAY_SERVER_ERROR_AT.FIELD} />

      <FormField
        error={!!errorObj[inputName]}
        description={<FormHelp error={!!errorObj[inputName]}>{errorObj[inputName]}</FormHelp>}
      >
        <Label required={required}>{label}</Label>
        <Textarea
          name={inputName}
          value={inputs[inputName]}
          placeholder={placeholder}
          rows={10}
          onChange={handleChange}
          onBlur={save}
          error={displayError || Boolean(errorObj[inputName])}
        />
        <FormHelp error={displayError}>{displayError && serverErrorObj.message}</FormHelp>
        {!errorObj[inputName] && inputs[inputName] && includeDescriptionStrength && (
          <PropertyDescriptionStrength description={inputs[inputName]} />
        )}
      </FormField>
    </div>
  );
};

TextAreaFormField.propTypes = propTypes;
TextAreaFormField.defaultProps = defaultProps;

export default TextAreaFormField;
